import { useState, useMemo, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  CheckCircle2,
  AlertTriangle,
  TrendingUp,
  Zap,
  Activity,
  ChevronRight,
  Info,
  ChevronDown,
  ShieldAlert,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import WeekTypeBadge from "@/components/planning/WeekTypeBadge";
import type { WeekType } from "@/types/weekTypes";
import { computePrioritizedAlert, type PrioritizedAlert } from "@/lib/alertPriority";
import {
  recordAlerts,
  analyzeRecurrence,
  getRecurrenceModifier,
  type AlertCategory,
} from "@/lib/alertHistory";
import { shouldPulse, markAlertSeen, getIntensityFactor } from "@/lib/alertSeen";
import StatusActionPanel from "./StatusActionPanel";

type ActiveContext = string;

// ── Types ────────────────────────────────────────────

export interface StatusIndicatorProps {
  hasPlanning: boolean;
  programTitle: string | null;
  weekType: WeekType | null;
  freeMode: boolean;
  pendingProposals: number;
  hasChaos: boolean;
  fatigueScore: number | null;
  isOverride: boolean;
  fatigueFactors: string[];
  fatigueReliable: boolean;
  consecutiveTrainingDays: number;
  missedSessionDays: number;
  recentPainDays: number;
  activeTab: ActiveContext;
}

const COLLAPSED_KEY = "status-indicator-collapsed";

// ── Visual config per status ─────────────────────────

type VisualStatus = PrioritizedAlert["status"];

const STATUS_CONFIG: Record<VisualStatus, { icon: typeof CheckCircle2; color: string; bg: string; pulseColor: string }> = {
  ok: {
    icon: CheckCircle2,
    color: "text-primary",
    bg: "bg-primary/10 border-primary/30",
    pulseColor: "shadow-[0_0_0_0_hsl(var(--primary)/0.4)]",
  },
  incomplete: {
    icon: AlertTriangle,
    color: "text-amber-500",
    bg: "bg-amber-500/5 border-amber-500/20",
    pulseColor: "shadow-[0_0_0_0_hsl(38,92%,50%,0.4)]",
  },
  overload: {
    icon: TrendingUp,
    color: "text-destructive",
    bg: "bg-destructive/5 border-destructive/20",
    pulseColor: "shadow-[0_0_0_0_hsl(var(--destructive)/0.4)]",
  },
  chaos: {
    icon: Zap,
    color: "text-amber-500",
    bg: "bg-amber-500/5 border-amber-500/20",
    pulseColor: "shadow-[0_0_0_0_hsl(38,92%,50%,0.4)]",
  },
  unknown: {
    icon: Info,
    color: "text-muted-foreground",
    bg: "bg-muted/30 border-border",
    pulseColor: "",
  },
};

// Priority-level icon override for P1 (health)
function getAlertIcon(alert: PrioritizedAlert) {
  if (alert.category === "health") return ShieldAlert;
  return STATUS_CONFIG[alert.status].icon;
}

// ── Component ────────────────────────────────────────

export default function DashboardStatusIndicator(props: StatusIndicatorProps) {
  const alert = useMemo(() => computePrioritizedAlert(props), [props]);

  const config = STATUS_CONFIG[alert.status];
  const Icon = getAlertIcon(alert);

  const factCount = props.fatigueFactors.length;

  // ── Recurrence analysis ──
  const recurrence = useMemo(() => analyzeRecurrence(), [alert.category]);

  const recurrenceMod = useMemo(
    () => getRecurrenceModifier(alert.category, recurrence),
    [alert.category, recurrence]
  );

  // ── Record alerts to history ──
  const recordedRef = useRef<string | null>(null);
  useEffect(() => {
    const allCategories: AlertCategory[] = [
      alert.category,
      ...alert.silenced.map((s) => s.category),
    ];
    const key = `${new Date().toISOString().slice(0, 10)}-${allCategories.sort().join(",")}`;
    if (recordedRef.current !== key) {
      recordAlerts(allCategories, props.activeTab, alert.priority);
      recordedRef.current = key;
    }
  }, [alert, props.activeTab]);

  // ── Pulse logic ──
  const isPulsing = useMemo(
    () => alert.priority <= 3 && shouldPulse(alert.category, alert.priority),
    [alert.category, alert.priority]
  );

  // ── Action panel state ──
  const [actionPanelOpen, setActionPanelOpen] = useState(false);

  // ── Intensity (dims after seen) ──
  const intensity = useMemo(
    () => getIntensityFactor(alert.category, alert.priority),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [alert.category, alert.priority, actionPanelOpen]
  );

  const handleToggleActions = () => {
    const next = !actionPanelOpen;
    setActionPanelOpen(next);
    if (next) {
      markAlertSeen(alert.category, alert.priority);
    }
  };

  const handleCloseActions = () => {
    setActionPanelOpen(false);
  };

  // ── Collapse logic ──
  const [collapsed, setCollapsed] = useState(
    () => localStorage.getItem(COLLAPSED_KEY) === "true"
  );
  const prevPriorityRef = useRef(alert.priority);

  // Auto-expand when priority escalates
  useEffect(() => {
    if (alert.priority < prevPriorityRef.current) {
      setCollapsed(false);
      localStorage.setItem(COLLAPSED_KEY, "false");
    }
    prevPriorityRef.current = alert.priority;
  }, [alert.priority]);

  const toggleCollapse = (e: React.MouseEvent) => {
    e.stopPropagation();
    const next = !collapsed;
    setCollapsed(next);
    localStorage.setItem(COLLAPSED_KEY, String(next));
  };

  // Pulse animation speed: P1 = fast, P2 = medium, P3 = slow
  const pulseSpeed = alert.priority <= 1 ? "1s" : alert.priority <= 2 ? "2s" : "3s";

  return (
    <div className="space-y-1.5">
      <div
        onClick={handleToggleActions}
        className={cn(
          "rounded-xl border px-3 sm:px-4 overflow-hidden transition-all cursor-pointer",
          "bg-white/[0.03] backdrop-blur-sm border-white/[0.08] shadow-[inset_0_1px_0_0_rgba(255,255,255,0.04)]",
          // Pulse glow animation
          isPulsing && !actionPanelOpen && "animate-status-pulse",
          // Hover effect
          "hover:bg-white/[0.06] active:scale-[0.995]",
        )}
        style={{
          // Green/ok stays fully opaque; only dim warning+ after seen
          opacity: alert.status === "ok" || alert.status === "unknown"
            ? 1
            : isPulsing ? 1 : intensity < 1 ? Math.max(intensity, 0.65) : 1,
          transition: "opacity 0.5s ease",
          ...(isPulsing && !actionPanelOpen ? {
            animationDuration: pulseSpeed,
            "--status-pulse-color": alert.priority <= 1
              ? "hsl(0 72% 51% / 0.4)"
              : alert.priority <= 2
                ? "hsl(38 92% 50% / 0.4)"
                : "hsl(82 85% 55% / 0.3)",
          } as React.CSSProperties : {}),
        }}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") handleToggleActions(); }}
      >
        {/* ── Always-visible header ── */}
        <div className="flex items-center gap-2 py-2 min-w-0 overflow-hidden">
          <motion.div
            animate={isPulsing && !actionPanelOpen ? {
              scale: [1, 1.2, 1],
            } : {}}
            transition={{
              duration: alert.priority <= 1 ? 0.6 : 1,
              repeat: Infinity,
              ease: "easeInOut",
            }}
          >
            <Icon className={cn("h-4 w-4 shrink-0", config.color)} />
          </motion.div>
          <span className={cn("text-xs font-semibold shrink-0", config.color)}>
            {alert.label}
          </span>

          {/* Collapsed: fatigue badge + primary message inline */}
          {collapsed && (
            <>
              {props.fatigueScore !== null && (
                <Badge
                  variant="outline"
                  className={cn(
                    "text-[10px] px-1.5 py-0 tabular-nums gap-1 shrink-0",
                    props.fatigueScore >= 7
                      ? "border-destructive/30 text-destructive"
                      : props.fatigueScore >= 5
                        ? "border-amber-500/30 text-amber-500"
                        : "border-border text-muted-foreground"
                  )}
                >
                  <Activity className="h-3 w-3" />
                  {props.fatigueScore.toFixed(0)}/10
                </Badge>
              )}
              <span className="text-[11px] text-muted-foreground truncate min-w-0 flex-1">
                {alert.message}
              </span>
            </>
          )}

          {/* Expanded: spacer + badges */}
          {!collapsed && <div className="flex-1 min-w-0" />}
          {!collapsed && props.weekType && <WeekTypeBadge type={props.weekType} />}
          {!collapsed && props.programTitle ? (
            <Badge variant="outline" className="text-[10px] border-primary/30 text-primary px-1.5 py-0 truncate max-w-[100px] sm:max-w-[120px]">
              {props.programTitle}
            </Badge>
          ) : !collapsed && props.freeMode ? (
            <Badge variant="secondary" className="text-[10px] px-1.5 py-0 shrink-0">
              Mode libre
            </Badge>
          ) : null}

          <button
            onClick={toggleCollapse}
            className="shrink-0 rounded-md p-1 hover:bg-background/60 transition-colors"
            title={collapsed ? "Développer" : "Réduire"}
          >
            <motion.div
              animate={{ rotate: collapsed ? 0 : 180 }}
              transition={{ duration: 0.2 }}
            >
              <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
            </motion.div>
          </button>
        </div>

        {/* ── Expandable details ── */}
        <AnimatePresence initial={false}>
          {!collapsed && (
            <motion.div
              key="details"
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.25, ease: "easeInOut" }}
              className="overflow-hidden"
            >
              <div className="pb-3 space-y-2">
                {/* ── Silenced alerts (secondary, shown as muted causes) ── */}
                {alert.silenced.length > 0 && (
                  <div className="space-y-0.5">
                    {alert.silenced.slice(0, 3).map((s, i) => (
                      <p key={i} className="text-[9px] text-muted-foreground/70 leading-tight flex items-start gap-1">
                        <span className="shrink-0 mt-0.5 h-0.5 w-0.5 rounded-full bg-muted-foreground/40" />
                        {s.message}
                      </p>
                    ))}
                  </div>
                )}

                {/* ── Recurrence indicator ── */}
                {recurrenceMod.prefix && (
                  <p className="text-[10px] italic text-muted-foreground/80 leading-tight pl-3">
                    {recurrenceMod.prefix}
                  </p>
                )}

                {/* ── Primary action + fatigue gauge ── */}
                <div className="flex items-center gap-2 pt-0.5">
                  <p className="text-[11px] font-medium text-foreground flex items-center gap-1.5 shrink-0">
                    <ChevronRight className="h-3 w-3 shrink-0 text-primary" />
                    {alert.message}
                  </p>

                  {props.fatigueScore !== null && (
                    <div className="flex-1 flex items-center gap-1.5 min-w-[60px]">
                      <div className="h-1.5 flex-1 rounded-full bg-muted/50 overflow-hidden">
                        <motion.div
                          className={cn(
                            "h-full rounded-full",
                            props.fatigueScore >= 7 ? "bg-destructive"
                              : props.fatigueScore >= 5 ? "bg-amber-500"
                              : "bg-primary"
                          )}
                          initial={{ width: 0 }}
                          animate={{ width: `${(props.fatigueScore / 10) * 100}%` }}
                          transition={{ duration: 0.5, ease: "easeOut" }}
                        />
                      </div>
                      <span className={cn(
                        "text-[10px] font-semibold tabular-nums shrink-0",
                        props.fatigueScore >= 7 ? "text-destructive"
                          : props.fatigueScore >= 5 ? "text-amber-500"
                          : "text-muted-foreground"
                      )}>
                        {props.fatigueScore.toFixed(0)}/10
                      </span>
                    </div>
                  )}

                  {factCount > 0 && props.fatigueReliable && (
                    <Popover>
                      <PopoverTrigger asChild>
                        <button
                          onClick={(e) => e.stopPropagation()}
                          className="text-[10px] text-muted-foreground hover:text-foreground transition-colors shrink-0"
                        >
                          {factCount} signaux
                        </button>
                      </PopoverTrigger>
                      <PopoverContent align="end" className="w-64 text-xs space-y-2">
                        <p className="font-semibold text-foreground">Détail du raisonnement</p>
                        <ul className="space-y-1">
                          {props.fatigueFactors.map((f, i) => (
                            <li key={i} className="text-muted-foreground flex items-start gap-1.5">
                              <span className="shrink-0 mt-1 h-1 w-1 rounded-full bg-muted-foreground/50" />
                              {f}
                            </li>
                          ))}
                        </ul>
                        <p className="text-muted-foreground/70 pt-1 border-t border-border">
                          Score calculé sur les 7 derniers jours — lecture seule.
                        </p>
                      </PopoverContent>
                    </Popover>
                  )}
                </div>

                {/* ── Click hint ── */}
                <p className="text-[9px] text-primary/60 text-center pt-0.5">
                  Cliquer pour voir les actions recommandées
                </p>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* ── Action panel (slides in below) ── */}
      <AnimatePresence>
        {actionPanelOpen && (
          <StatusActionPanel
            alert={alert}
            fatigueScore={props.fatigueScore}
            hasProgram={!!props.programTitle}
            hasPlanning={props.hasPlanning}
            onClose={handleCloseActions}
          />
        )}
      </AnimatePresence>
    </div>
  );
}
